// Shared constants + contracts between client and server.
// Keep this file dependency-free so both bundles can import it.

export const ARENA_ROOM = "arena";
export const APP_NAME = "JACE.GAMES";

export type Phase = "lobby" | "countdown" | "playing" | "results";

/** Per-tick input snapshot a client sends while a real-time game is running. */
export interface GameInput {
  mx: number;
  my: number;
  punch: boolean;
}

export type GameCategory = "arcade" | "board" | "word" | "hype";

/** how a room is being played: alone, against the CPU, 1v1 with a friend, or a
 * 3-12 player party (several phones, and/or one phone passed around) */
export type PlayMode = "solo" | "cpu" | "1v1" | "party";
export const PLAY_MODES: { id: PlayMode; name: string; verb: string }[] = [
  { id: "solo", name: "Solo", verb: "Play solo" },
  { id: "cpu", name: "Computer", verb: "Play the computer" },
  { id: "1v1", name: "Friend", verb: "Play a friend" },
  { id: "party", name: "Party", verb: "Start a party" },
];

export interface GameMeta {
  id: string;
  name: string;
  blurb: string;
  category: GameCategory;
  /** realtime = server sim loop + countdown; turn = event-driven; solo = 1 player, no opponent */
  kind: "realtime" | "turn" | "solo";
  /** which play modes this game supports */
  modes: PlayMode[];
  /** pure luck — no skill, so the CPU difficulty picker is meaningless here */
  luckOnly?: boolean;
  /** solo runs of this game can be posted to a global leaderboard */
  leaderboard?: boolean;
  /** what the leaderboard score is measured in (shown as a column header) */
  unit?: string;
  /** single emoji glyph used for lobby art tiles */
  icon: string;
  /** accent color for the lobby art tile, hex */
  tint: string;
}

export const CATEGORIES: { id: GameCategory | "all"; name: string }[] = [
  { id: "all", name: "All" },
  { id: "arcade", name: "Arcade" },
  { id: "board", name: "Board" },
  { id: "word", name: "Word" },
  { id: "hype", name: "Hype" },
];

export const GAMES: GameMeta[] = [
  {
    id: "boxing",
    name: "Boxing",
    blurb: "A top-down ring brawl — circle your rival, slip their punches and land your own. First to 100 points, or ahead when the bell rings at 90 seconds.",
    category: "arcade",
    kind: "realtime",
    modes: ["cpu", "1v1"],
    icon: "🥊",
    tint: "#ff6b4a",
  },
  {
    id: "checkers",
    name: "Checkers",
    blurb: "English draughts on an 8×8 board. Forced captures, chain your jumps, crown a man on the back rank. Last player able to move wins.",
    category: "board",
    kind: "turn",
    modes: ["cpu", "1v1"],
    icon: "⚫",
    tint: "#d4a24c",
  },
  {
    id: "snl",
    name: "Snakes & Ladders",
    blurb: "The classic 10×10 board. Roll, climb the ladders, dodge the snakes, first to square 100 wins outright. Roll a 6 and you go again.",
    category: "board",
    kind: "turn",
    modes: ["cpu", "1v1"],
    luckOnly: true,
    icon: "🎲",
    tint: "#4cd4a0",
  },
  {
    id: "bubble",
    name: "Bubble Blast",
    blurb: "Solo bubble shooter — drag to aim, release to fire. Match 3 or more to clear them, endless levels get denser. Earn bombs, missiles and ballistic strikes. Progress saves automatically.",
    category: "arcade",
    kind: "solo",
    modes: ["solo"],
    leaderboard: true,
    unit: "score",
    icon: "🫧",
    tint: "#4ac9ff",
  },
  {
    id: "tetris",
    name: "Block Drop",
    blurb: "Stack and clear falling blocks. Solo: marathon until you top out, gravity ramps every level. 2P: two wells side by side — clearing lines buries your rival in garbage.",
    category: "arcade",
    kind: "realtime",
    modes: ["solo", "1v1"],
    leaderboard: true,
    unit: "score",
    icon: "🧱",
    tint: "#8b5cf6",
  },
  {
    id: "snake",
    name: "Snake",
    blurb: "Eat, grow, don't crash. Swipe to steer. Solo: chase your best length against the wall. 2P: one shared board — crash into your rival's body and they score the kill.",
    category: "arcade",
    kind: "realtime",
    modes: ["solo", "1v1"],
    leaderboard: true,
    unit: "score",
    icon: "🐍",
    tint: "#37d67a",
  },
  {
    id: "tictactoe",
    name: "Tic Tac Toe",
    blurb: "Three in a row on a 3×3 grid. X goes first, and you swap who starts every rematch. Play the computer, a friend, or two of you on one phone.",
    category: "board",
    kind: "turn",
    modes: ["cpu", "1v1"],
    icon: "❌",
    tint: "#ff6cc4",
  },
  {
    id: "chess",
    name: "Chess",
    blurb: "The classic, full rules enforced server-side. Tap a piece to see its legal moves, board auto-flips for black. White moves first; checkmate, stalemate, or resign ends it.",
    category: "board",
    kind: "turn",
    modes: ["cpu", "1v1"],
    icon: "♟️",
    tint: "#e7ecf5",
  },
  {
    id: "wordduel",
    name: "Word Duel",
    blurb: "Same hidden 5-letter word, you both race to guess it with an on-screen keyboard. See your rival's tile colors, never their letters. Solo: endless — one miss ends the run.",
    category: "word",
    kind: "turn",
    modes: ["solo", "cpu", "1v1"],
    leaderboard: true,
    unit: "score",
    icon: "🟩",
    tint: "#f0e14c",
  },
  {
    id: "shred",
    name: "Shred",
    blurb: "A grail with a shared health bar — mash the screen to deal damage faster than your rival. Land the killing blow to take the round; best of 5, first to 3 wins.",
    category: "hype",
    kind: "realtime",
    modes: ["cpu", "1v1"],
    icon: "⚔️",
    tint: "#ff4a6b",
  },
  {
    id: "asteroids",
    name: "Rock Blaster",
    blurb: "Twin-stick classic — drag to fly, tap FIRE to shoot. Solo: 3 lives, rocks split and speed up each level. 2P: rocks score points, shooting your rival scores more.",
    category: "arcade",
    kind: "realtime",
    modes: ["solo", "1v1"],
    leaderboard: true,
    unit: "score",
    icon: "☄️",
    tint: "#9ca3af",
  },
  {
    id: "invaders",
    name: "Invaders",
    blurb: "The wave marches down and speeds up as it thins, dropping bombs the whole way. Solo: survive as many waves as you can, 3 lives. 2P: outlast or outscore your partner.",
    category: "arcade",
    kind: "realtime",
    modes: ["solo", "1v1"],
    leaderboard: true,
    unit: "score",
    icon: "👾",
    tint: "#4affa0",
  },
  {
    id: "hormuz",
    name: "Strait of Tastuz",
    blurb: "Portrait missile defense — tap above your destroyer to aim, hold FIRE to launch. Escort the tankers past drones, missiles, mines, and coastal launch sites before your ships burn.",
    category: "arcade",
    kind: "realtime",
    modes: ["solo"],
    leaderboard: true,
    unit: "score",
    icon: "🚢",
    tint: "#4a90ff",
  },
  {
    id: "worms",
    name: "Artillery Worms",
    blurb: "Side-view turn-based artillery. Pick a bomb — small flies far, large hits hard — drag to aim, tap FIRE to launch over destructible terrain and flatten their fort. First worm to 0 HP loses.",
    category: "arcade",
    kind: "turn",
    modes: ["cpu", "1v1"],
    icon: "💣",
    tint: "#a0724c",
  },
  {
    id: "tug",
    name: "Tug of War",
    blurb: "A 1-D rope tug — flick or tap fast to haul it your way against wind and mud. Solo: how many escalating levels can you clear against a ramping auto-puller. 2P / CPU: best of 3.",
    category: "arcade",
    kind: "realtime",
    modes: ["solo", "cpu", "1v1"],
    leaderboard: true,
    unit: "levels",
    icon: "🪢",
    tint: "#c9924c",
  },
  {
    id: "wordbattle",
    name: "Word Battle",
    blurb: "Boggle-style 4×4 letter grid, both players race the same board. Drag across adjacent letters — longer words score more. Earn Freeze and Steal power-ups. Solo: 75-second time attack.",
    category: "word",
    kind: "realtime",
    modes: ["solo", "cpu", "1v1"],
    leaderboard: true,
    unit: "score",
    icon: "🔡",
    tint: "#4affd2",
  },
  {
    id: "solitaire",
    name: "You Don't Have the Cards",
    blurb: "Classic Klondike — draw one from the stock, build tableau runs down in alternating colors, send suits up to the foundations. No timer, no opponent, just you and the deck.",
    category: "board",
    kind: "solo",
    modes: ["solo"],
    leaderboard: true,
    unit: "score",
    icon: "🃏",
    tint: "#e0b84c",
  },
  {
    id: "colorblock",
    name: "Color Block Blaster",
    blurb: "Drag blocks onto an 8×8 grid to fill full rows or columns and clear them. No rotating — place pieces exactly as shown. Game ends when nothing in your tray of 3 fits anywhere.",
    category: "arcade",
    kind: "solo",
    modes: ["solo"],
    leaderboard: true,
    unit: "score",
    icon: "🧩",
    tint: "#ff6f3c",
  },
  {
    id: "jaywalk",
    name: "Jaywalk",
    blurb: "Hop a little blue jay across an endless town: busy roads, rivers of drifting logs, lily pads and train tracks. Tap to hop, swipe to hop sideways or back. Grab seeds to charge a Flutter that sails over a whole lane, and don't dawdle, the cat is right behind you.",
    category: "arcade",
    kind: "solo",
    modes: ["solo"],
    leaderboard: true,
    unit: "rows",
    icon: "🐦",
    tint: "#4a7cff",
  },
  {
    id: "minigolf",
    name: "Mini Golf",
    blurb: "Drag back and release to putt. 4 holes, each with its own obstacles — walls, gaps, and a tight corridor by the end. Lowest total strokes across all holes wins.",
    category: "arcade",
    kind: "turn",
    modes: ["cpu", "1v1"],
    icon: "⛳",
    tint: "#7ed957",
  },
  {
    id: "imposter",
    name: "Imposter",
    blurb: "Everyone gets the same secret word — except the imposter, who has to bluff. Give a clue, spot who’s faking it, then vote them out. 3–12 players: share one phone and play out loud, or join from your own phones.",
    category: "word",
    kind: "turn",
    modes: ["party"],
    icon: "🕵️",
    tint: "#9b6dff",
  },
  {
    id: "doodle",
    name: "Doodle Dash",
    blurb: "One player draws a secret word, everyone else races to guess it. Sooner is worth more, and the drawer scores for every right guess. 3–12 players: guess on your own phones, or pass one phone and shout it out.",
    category: "word",
    kind: "turn",
    modes: ["party"],
    icon: "🖍️",
    tint: "#ff6cc4",
  },
  {
    id: "act",
    name: "Act It Out",
    blurb: "One player acts out as many secret words as they can before the clock runs out, everyone else shouts guesses. No talking, no pointing at things! 3–12 players: one phone held up by the actor, or each on their own phone.",
    category: "word",
    kind: "turn",
    modes: ["party"],
    icon: "🎭",
    tint: "#ffb547",
  },
  {
    id: "relay",
    name: "Doodle Relay",
    blurb: "Draw a word, pass it on: the next player guesses your drawing, the next draws their guess, and so on round the table. Then watch how far every word drifted. 3–12 players: own phones at once, or pass one phone.",
    category: "word",
    kind: "turn",
    modes: ["party"],
    icon: "🔁",
    tint: "#4d7cff",
  },
  {
    id: "same",
    name: "Same Brain",
    blurb: "Everyone answers the same prompt in secret, like “a fruit that’s yellow”. Score for every player who said the same thing as you. Think like the room! 3–12 players: own phones, or pass one phone.",
    category: "word",
    kind: "turn",
    modes: ["party"],
    icon: "🧠",
    tint: "#37d67a",
  },
  {
    id: "crunch",
    name: "Number Crunch",
    blurb: "Quick-fire math for the whole room. Race everyone on the same problem, or take turns at a 45-second sprint. Four levels from simple sums to brain-benders, and everyone can pick their own. 3–12 players: own phones, or pass one phone.",
    category: "word",
    kind: "turn",
    modes: ["party"],
    icon: "🔢",
    tint: "#ffd84d",
  },
  {
    id: "phrase",
    name: "Phrase Race",
    blurb: "A hidden phrase and a clue, and its letters turn over one at a time. Type it the moment you see it: the fewer letters showing, the more you score. 3–12 players: own phones, or pass one phone and shout it out.",
    category: "word",
    kind: "turn",
    modes: ["party"],
    icon: "🔤",
    tint: "#ff4fa3",
  },
];

/** game ids whose solo runs can be posted to the leaderboard */
export const LEADERBOARD_IDS = new Set(GAMES.filter((g) => g.leaderboard).map((g) => g.id));

export type BotLevel = "easy" | "medium" | "hard";
export const BOT_LEVELS: { id: BotLevel; name: string; blurb: string }[] = [
  { id: "easy", name: "Easy", blurb: "learning the ropes" },
  { id: "medium", name: "Medium", blurb: "a real fight" },
  { id: "hard", name: "Hard", blurb: "no mercy" },
];

/** party rooms: how many seats a game can have (a seat is a person — a phone
 * of their own, or a "local" player sharing the host's phone) */
export const PARTY = { MIN: 3, MAX: 12 };

export const CODE_LENGTH = 4;
export const NAME_MAX = 14;
